import { useEffect } from 'react';
import { useRouteMatch } from 'react-router';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import { Button, Divider, Form, message, Modal, Space } from 'antd';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';

import { useCustomerGeneral } from '@/core/domain/people/Customers/Customers.hooks';

import { CustomerContact } from '@/core/domain/people/CustomerContacts/CustomerContacts.types';
import {
  useCreateCustomerContact,
  useCustomerContactDetailRemove,
  useCustomerContacts,
  useDeleteCustomerContact,
  useUpdateCustomerContact,
} from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';

import { PageProps } from '@/types/PageProps';

import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import { ColumnsType } from '@/components/_common/Table';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import {
  InfiniteTable,
  MultipleContactModal,
  SaveButton,
} from '@/components/_common';
import { useDisclosure } from '@/hooks';
import { getTableScrollX } from '@/utils/table';
import moment from 'moment';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useCustomerFormContext } from '@/components/Customers/CustomerFormProvider';
import CustomerFooter from '@/components/Customers/CustomerRepeatedButtons';
import Authorities from '@/types/Authority';
import { removeMaskDetailList } from '@/utils/mask/removeMaskDetailList';
import { useContactColumns } from '@/hooks/useContactColumns';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { isAppPortocar } from '@/index';
import { ContactDetailList } from '@/core/domain/people/_shared/Contact';
import { ContactType } from '@/core/domain/_shared/ContactType';

const CustomerContactsPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const { setParamLabel } = useBreadcrumbs();
  const { customerId } = useRouteMatch<{ customerId: string }>().params;
  const { handleSubmit, disabled, isLoading } = useCustomerFormContext();
  const { columns: columnsContact } = useContactColumns<CustomerContact>();

  const [viewContact, setViewContact] = useDisclosure();

  const { data: customer, isFetching: isFetchingCustomer } = useCustomerGeneral(
    +customerId,
  );

  useEffect(() => {
    if (customer && !isEmpty(customer)) {
      setParamLabel(':customerId', customer.name);
    }
  }, [customer, isFetchingCustomer]);

  const customerContacts = useCustomerContacts(Number(customerId));
  const createContactMutation = useCreateCustomerContact();
  const updateContactMutation = useUpdateCustomerContact();
  const deleteContactMutation = useDeleteCustomerContact();
  const customerContactDetailRemove = useCustomerContactDetailRemove();

  useEffect(() => {
    if (customerContacts.data?.length && viewContact) {
      const contactUpdated = customerContacts.data.find(
        (contact) => contact.id === form.getFieldValue('contactId'),
      );

      if (contactUpdated) {
        handleContactSelect(contactUpdated);
      }
    }
  }, [customerContacts.dataUpdatedAt, viewContact]);

  async function handleSave() {
    try {
      const { contactId, ...values } = await form.validateFields();

      const detailList = removeMaskDetailList(values.detailList);

      if (contactId) {
        await updateContactMutation.mutateAsync({
          ...values,
          detailList,
          customerId: Number(customerId),
          id: contactId,
        });
      } else {
        await createContactMutation.mutateAsync({
          value: { ...values, detailList },
          customerId: Number(customerId),
        });
      }

      setViewContact.close();
      handleContactSelect(undefined);
    } catch {}
  }

  async function handleRemoveContactDetail(detailId: number) {
    if (isAppPortocar) {
      const formValues = form.getFieldsValue();

      const detailList =
        (formValues.detailList as ContactDetailList[]) || undefined;

      const contactType = detailList?.find(
        (detail) => detail.id === detailId,
      )?.contactType;

      const isEmail = contactType === ContactType.Email;

      const emails = detailList.filter(
        (detail) =>
          detail.id !== detailId && detail.contactType === ContactType.Email,
      );

      const phones = detailList.filter(
        (detail) =>
          detail.id !== detailId &&
          (detail.contactType === ContactType.Mobile ||
            detail.contactType === ContactType.Phone ||
            detail.contactType === ContactType.WhatsApp),
      );

      if (isEmail ? emails.length === 0 : phones.length === 0) {
        message.destroy();
        message.warning(
          `O cadastro requer pelo menos um ${
            contactType === ContactType.Email ? 'e-mail' : 'telefone'
          } obrigatório.`,
        );
        return;
      }
    }

    await customerContactDetailRemove.mutateAsync({
      customerId: Number(customerId),
      detailId,
    });

    customerContacts.refetch();
  }

  const handleRemoveClick = (contact: CustomerContact) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'customers.contact.remove',
      }),
      content: `${intl.formatMessage({
        id: 'customers.contact.remove.alert',
      })} ${contact.name}`,
      onOk: async () => {
        if (isAppPortocar && customerContacts.data?.length === 1) {
          message.destroy();
          message.warning(
            'O cadastro requer pelo menos um item de contatos obrigatório.',
          );
        } else {
          await deleteContactMutation.mutateAsync({
            customerId: contact.customerId,
            contactId: contact.id,
          });
        }
      },
      okType: 'danger',
    });
  };

  function handleContactSelect(employeeContact?: CustomerContact) {
    form.setFieldsValue({
      ...employeeContact,
      main: employeeContact?.main,
      detailList: employeeContact?.detailList,
      contactId: employeeContact?.id,
      birthDate: employeeContact?.birthDate
        ? moment(employeeContact?.birthDate)
        : null,
    });
  }

  const columns: ColumnsType<CustomerContact> = [
    ...columnsContact,
    {
      key: 'action',
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      align: 'right',
      fixed: 'right',
      width: 50,
      render: (_, row: CustomerContact) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => {
                handleContactSelect(row);
                setViewContact.open();
              },
            }}
          />

          <Divider type="vertical" />

          <DeleteIcon
            disabled={!hasPermissions([Authorities.REGISTER_CUSTOMER_DELETE])}
            onClick={() => handleRemoveClick(row)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs
        permissions={route.permissions}
        route={route.parent}
        baseKey={customerId}
        basePath={`/app/customers/${customerId}`}
        extra={
          <SpaceForButton>
            <Button
              block
              type="primary"
              onClick={setViewContact.open}
              disabled={!hasPermissions([Authorities.REGISTER_CUSTOMER_EDIT])}
            >
              <FormattedMessage id="commons.entity.add" />
            </Button>
            <SaveButton
              block
              disabled={
                disabled ||
                !hasPermissions([Authorities.REGISTER_CUSTOMER_EDIT])
              }
              loading={isLoading}
              onClick={handleSubmit}
            >
              <FormattedMessage id="commons.entity.save" />
            </SaveButton>
          </SpaceForButton>
        }
      >
        <InfiniteTable
          dataSource={customerContacts.data}
          rowKey={(row) => row.id}
          columns={columns}
          loading={customerContacts.isFetching}
          pagination={false}
          scroll={{ x: getTableScrollX(columns) }}
        />

        <CustomerFooter customerId={Number(customerId)} disabled={disabled} />

        <MultipleContactModal
          open={viewContact}
          onCancel={setViewContact.close}
          onOk={handleSave}
          confirmLoading={
            createContactMutation.isLoading || updateContactMutation.isLoading
          }
          form={form}
          personType={
            customer
              ? customer.personType === 'NATURAL'
                ? 'NaturalPerson'
                : 'LegalPerson'
              : 'LegalPerson'
          }
          onDetailRemove={handleRemoveContactDetail}
        />
      </DefaultRouteTabs>
    </>
  );
};

export default CustomerContactsPage;
